import CameraNotificationRules from '../../components/cameras/CameraNotificationRules';
import CameraEventAudio from '../../components/cameras/CameraEventAudio';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { ArrowLeft, Edit2, Camera as CameraIcon } from 'lucide-react';
import { cameraService } from '../../services';
import type { Camera } from '../../types';

const ViewCameraPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [camera, setCamera] = useState<Camera | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchCamera(parseInt(id));
    }
  }, [id]);

  const fetchCamera = async (cameraId: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await cameraService.getCameraById(cameraId);
      setCamera(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch camera');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <Loading size="lg" />
        </div>
      </MainLayout>
    );
  }

  if (error || !camera) {
    return (
      <MainLayout>
        <Alert variant="error" title="Error" message={error || 'Camera not found'} />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/cameras')}
              leftIcon={<ArrowLeft size={16} />}
            >
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">Camera Details</h1>
              <p className="text-text-muted">{camera.name || camera.camera_location}</p>
            </div>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate(`/cameras/${id}/edit`)}
            leftIcon={<Edit2 size={16} />}
          >
            Edit Camera
          </Button>
        </div>

        {/* Status Badge */}
        <Card>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              camera.status ? 'bg-status-success/10' : 'bg-surface-secondary'
            }`}>
              <CameraIcon size={24} className={camera.status ? 'text-status-success' : 'text-text-muted'} />
            </div>
            <div>
              <Badge variant={camera.status ? 'success' : 'danger'} dot size="lg">
                {camera.status ? 'Online' : 'Offline'}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Basic Information */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-text-muted">Camera Name</label>
              <p className="mt-1 text-text-primary">{camera.name || camera.camera_location || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted">Camera Channel</label>
              <p className="mt-1 text-text-primary">{camera.camera_channel || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted">Purpose</label>
              <p className="mt-1 text-text-primary">{camera.camera_purpose || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted">ID Camera</label>
              <p className="mt-1 text-text-primary">{camera.is_id_camera ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </Card>

        {/* Device Information */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Device Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-text-muted">Brand</label>
              <p className="mt-1 text-text-primary">{camera.brand || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted">Model</label>
              <p className="mt-1 text-text-primary">{camera.model || camera.camera_model || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted">Serial Number</label>
              <p className="mt-1 text-text-primary">{camera.serial_number || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted">Firmware Version</label>
              <p className="mt-1 text-text-primary">{camera.firmware_version || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted">MAC Address</label>
              <p className="mt-1 text-text-primary font-mono">{camera.camera_mac || '-'}</p>
            </div>
          </div>
        </Card>

        {/* Network Configuration */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Network Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-text-muted">IP Address</label>
              <p className="mt-1 text-text-primary font-mono">{camera.ip_address || camera.camera_ip || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted">HTTP Port</label>
              <p className="mt-1 text-text-primary">{camera.http_port || camera.camera_port || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted">ONVIF Port</label>
              <p className="mt-1 text-text-primary">{camera.onvif_port || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted">RTSP Port</label>
              <p className="mt-1 text-text-primary">{camera.rtsp_port || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted">Username</label>
              <p className="mt-1 text-text-primary">{camera.username || camera.camera_username || '-'}</p>
            </div>
          </div>
        </Card>

        {/* Protocol Configuration */}
        {camera.primary_protocol && (
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Protocol Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-text-muted">Primary Protocol</label>
                <p className="mt-1 text-text-primary">{camera.primary_protocol}</p>
              </div>
              {camera.supported_protocols && (
                <div>
                  <label className="text-sm font-medium text-text-muted">Supported Protocols</label>
                  <p className="mt-1 text-text-primary">{JSON.stringify(camera.supported_protocols)}</p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Connection Status */}
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Connection Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-text-muted">Last Seen</label>
              <p className="mt-1 text-text-primary">
                {camera.last_seen ? new Date(camera.last_seen).toLocaleString() : 'Never'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted">Last Health Check</label>
              <p className="mt-1 text-text-primary">
                {camera.last_health_check ? new Date(camera.last_health_check).toLocaleString() : 'Never'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted">Health Check Interval</label>
              <p className="mt-1 text-text-primary">{camera.health_check_interval || 30} seconds</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-muted">Event Polling Interval</label>
              <p className="mt-1 text-text-primary">{camera.event_polling_interval || 5} seconds</p>
            </div>
          </div>
        </Card>

        {/* Installation & Maintenance */}
        {(camera.camera_installation || camera.camera_last_maintenance) && (
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Installation & Maintenance</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {camera.camera_installation && (
                <div>
                  <label className="text-sm font-medium text-text-muted">Installation Date</label>
                  <p className="mt-1 text-text-primary">
                    {new Date(camera.camera_installation).toLocaleDateString()}
                  </p>
                </div>
              )}
              {camera.camera_last_maintenance && (
                <div>
                  <label className="text-sm font-medium text-text-muted">Last Maintenance</label>
                  <p className="mt-1 text-text-primary">
                    {new Date(camera.camera_last_maintenance).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </Card>
        )}
        {camera && <CameraEventAudio cameraId={camera.camera_id} />}
        {camera && <CameraNotificationRules cameraId={camera.camera_id} />}
      </div>
    </MainLayout>
  );
};

export default ViewCameraPage;
